import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { IAirports, ICarrier, ISegmentInformation } from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { EAirRatingDetailNames, EAirRatingDistributor, IAirRating } from "libraries/model/air-quote/IQuote";
import { Yii } from "libraries/interfaces/Yii";
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";

import { TLocalizedString } from "libraries/pipes/localized/localized.interface";
import { IColorScale } from "libraries/component/rating-notation/rating-notation.component";
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { defaultRatingConfiguration } from "libraries/component/rating-table/default-rating-configuration";

export interface IRatingConfiguration {
    distributor: EAirRatingDistributor;
    wording?: TLocalizedString;
    detailArray: IRatingConfigurationDetail[];
}

interface IRatingConfigurationDetail {
    code: EAirRatingDetailNames;
    nameArray: TLocalizedString;
    descriptionArray?: TLocalizedString;
    colorScaleArray?: IColorScale[];
}

@Component({
    selector: 'rating-table',
    template: `
        <ng-container *ngIf="journeys && configurationDetails">
            <table mat-table [dataSource]="journeys">
                <ng-container matColumnDef="direction">
                    <th-text *matHeaderCellDef [isTop]="true" [width]="firstWidth" [isLeft]="true">
                        <div class="table-title">
                            <ng-container *ngIf="globalRating.distributor !== distributors.yucangoo">{{title}}</ng-container>
                            <img *ngIf="globalRating.distributor === distributors.yucangoo"
                                 src="static/yucangoo_logo.png"
                                 [attr.alt]="title"
                                 [attr.title]="title">
                            <rating-notation [rating]="globalRating" [showAverage]="false" [small]="true"></rating-notation>
                        </div>
                    </th-text>
                    <td-text *matCellDef="let journey; let odd = odd; let index = index" [isOdd]="odd">
                        <div class="full-content">
                            <directions [from]="journey.from | findAirport:airports"
                                        [to]="journey.to | findAirport:airports"></directions>
                        </div>
                    </td-text>
                    <td-text *matFooterCellDef [isTop]="true" [text]="what"></td-text>
                </ng-container>

                <ng-container [matColumnDef]="detailNames.carbonFootprint">
                    <th-text *matHeaderCellDef [isLeft]="true" [isTop]="true"  [width]="width">
                        <div class="table-title inline">
                            <div>{{(configurationDetails | mapGet: detailNames.carbonFootprint).nameArray | localized}}</div>
                            <rating-notation [rating]="globalRating.detailArray | findDetail: detailNames.carbonFootprint"
                                             [showAverage]="false"
                                             [small]="true"
                                             [showShape]="true"
                                             [showNotation]="false"
                                             [colorScales]="(configurationDetails | mapGet: detailNames.carbonFootprint).colorScaleArray"></rating-notation>
                        </div>
                    </th-text>
                    <td-text *matCellDef="let journey; let odd = odd; let index = index"  [isOdd]="odd">
                        <div class="full-content inline">
                            <rating-notation [rating]="journey.rating.detailArray | findDetail: detailNames.carbonFootprint"
                                             [showAverage]="false"
                                             [small]="true"
                                             [showShape]="true"
                                             [showNotation]="false"
                                             [colorScales]="(configurationDetails | mapGet: detailNames.carbonFootprint).colorScaleArray"></rating-notation>
                            <div class="complementary-information">
                                <carbon-footprint [carbonValue]="journey.carbonFootprint"></carbon-footprint>
                            </div>
                        </div>
                    </td-text>
                    <td-text *matFooterCellDef [isTop]="true">
                        <div class="full-content small">
                            {{(configurationDetails | mapGet: detailNames.carbonFootprint).descriptionArray | localized}}
                        </div>
                    </td-text>

                </ng-container>

                <ng-container [matColumnDef]="detailNames.topCarrier">
                    <th-text *matHeaderCellDef [isLeft]="true" [isTop]="true"  [width]="width">
                        <div class="table-title inline">
                            <div>{{(configurationDetails | mapGet: detailNames.topCarrier).nameArray | localized}}</div>
                            <rating-notation [rating]="globalRating.detailArray | findDetail: detailNames.topCarrier"
                                             [showAverage]="false"
                                             [small]="true"
                                             [showShape]="true"
                                             [showNotation]="false"
                                             [colorScales]="(configurationDetails | mapGet: detailNames.topCarrier).colorScaleArray"></rating-notation>

                        </div>
                    </th-text>
                    <td-text *matCellDef="let journey; let odd = odd; let index = index" [isOdd]="odd">
                        <div class="full-content inline">
                            <rating-notation [rating]="journey.rating.detailArray | findDetail: detailNames.topCarrier"
                                             [showAverage]="false"
                                             [small]="true"
                                             [showShape]="true"
                                             [showNotation]="false"
                                             [colorScales]="(configurationDetails | mapGet: detailNames.topCarrier).colorScaleArray"></rating-notation>
                            <div>
                                <ng-container *ngFor="let code of journey.sector | getNoDoubleCarrierCode">
                                    <div class="complementary-information">
                                        {{code}} <ng-container *ngIf="carriers">({{carriers[code].name}})</ng-container>
                                    </div>
                                </ng-container>
                            </div>

                        </div>
                    </td-text>
                    <td-text *matFooterCellDef [isTop]="true">
                        <div class="full-content small">
                            {{(configurationDetails | mapGet: detailNames.topCarrier).descriptionArray | localized}}
                        </div>
                    </td-text>
                </ng-container>

                <ng-container [matColumnDef]="detailNames.punctuality">
                    <th-text *matHeaderCellDef [isLeft]="true" [isTop]="true"  [width]="width">
                        <div class="table-title inline">
                            <div>{{(configurationDetails | mapGet: detailNames.punctuality).nameArray | localized}}</div>
                            <rating-notation [rating]="globalRating.detailArray | findDetail: detailNames.punctuality"
                                             [showAverage]="false"
                                             [small]="true"
                                             [showShape]="true"
                                             [showNotation]="false"
                                             [colorScales]="(configurationDetails | mapGet: detailNames.punctuality).colorScaleArray"></rating-notation>
                        </div>
                    </th-text>
                    <td-text *matCellDef="let journey; let odd = odd; let index = index" [isOdd]="odd">
                        <div class="full-content inline">
                            <rating-notation [rating]="journey.rating.detailArray | findDetail: detailNames.punctuality"
                                             [showAverage]="false"
                                             [small]="true"
                                             [showShape]="true"
                                             [showNotation]="false"
                                             [colorScales]="(configurationDetails | mapGet: detailNames.punctuality).colorScaleArray"></rating-notation>
                            <div>
                                <ng-container *ngFor="let code of journey.sector | getNoDoubleCarrierCode">
                                    <div class="complementary-information">
                                        {{code}} <ng-container *ngIf="carriers">({{carriers[code].name}})</ng-container>
                                    </div>
                                </ng-container>
                            </div>

                        </div>
                    </td-text>
                    <td-text *matFooterCellDef [isTop]="true">
                        <div class="full-content small">
                            {{(configurationDetails | mapGet: detailNames.punctuality).descriptionArray | localized}}
                        </div>
                    </td-text>
                </ng-container>

                <ng-container [matColumnDef]="detailNames.routing">
                    <th-text *matHeaderCellDef [isLeft]="true" [isTop]="true"  [width]="width">
                        <div class="table-title inline">
                            <div>{{(configurationDetails | mapGet: detailNames.routing).nameArray | localized}}</div>
                            <rating-notation [rating]="globalRating.detailArray | findDetail: detailNames.routing"
                                             [showAverage]="false"
                                             [small]="true"
                                             [showShape]="true"
                                             [showNotation]="false"
                                             [colorScales]="(configurationDetails | mapGet: detailNames.routing).colorScaleArray"></rating-notation>

                        </div>
                    </th-text>
                    <td-text *matCellDef="let journey; let odd = odd; let index = index" [isOdd]="odd">
                        <div class="full-content inline">
                            <rating-notation [rating]="journey.rating.detailArray | findDetail: detailNames.routing"
                                             [showAverage]="false"
                                             [small]="true"
                                             [showShape]="true"
                                             [showNotation]="false"
                                             [colorScales]="(configurationDetails | mapGet: detailNames.routing).colorScaleArray"></rating-notation>
                            <div class="complementary-information">
                                <stops [nbStops]="journey.sector.length - 1"></stops>
                            </div>
                        </div>
                    </td-text>
                    <td-text *matFooterCellDef [isTop]="true">
                        <div class="full-content small">
                            {{(configurationDetails | mapGet: detailNames.routing).descriptionArray | localized}}
                        </div>
                    </td-text>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
                <tr mat-row *matFooterRowDef="displayedColumns"></tr>
            </table>

        </ng-container>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./rating-table.component.scss']
})

export class RatingTableComponent implements OnInit {

    @Input()
    public journeys: ISegmentInformation[];

    @Input()
    public globalRating: IAirRating;

    @Input()
    public airports: IAirports;

    @Input()
    public carriers: ICarrier;

    public readonly detailNames: typeof EAirRatingDetailNames = EAirRatingDetailNames;

    public readonly displayedColumns: string[] = [
        'direction',
        EAirRatingDetailNames.carbonFootprint,
        EAirRatingDetailNames.topCarrier,
        EAirRatingDetailNames.punctuality,
        EAirRatingDetailNames.routing,
    ];

    public title: string;
    public what: string;
    public configuration: IRatingConfiguration;

    public configurationDetails: Map<string, IRatingConfigurationDetail>;

    public distributors: typeof EAirRatingDistributor = EAirRatingDistributor;

    public width: string = '22%';
    public firstWidth: string = '12%';

    constructor(
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly changeDetector: ChangeDetectorRef) {
    }

    public ngOnInit(): void {
        if (this.globalRating) {
            switch (this.globalRating.distributor) {
                case EAirRatingDistributor.yucangoo:
                    this.title = Yii.t("wui-shared-js", "Note YUCANGoo");
                    this.what = Yii.t("wui-shared-js", "Que prend en compte YUCANGoo\u00A0?");
                    break;
                case EAirRatingDistributor.afidium:
                    this.title = Yii.t("wui-shared-js", "Que prend en compte AFIDIUM\u00A0?");
                    break;
                default:
                    this.title = Yii.t("wui-shared-js", "Que prend en compte la note\u00A0?");
            }

        }

        this.configurationResourceService.get(["wui", "shared_air", "config", "rating", "rating.json"], defaultRatingConfiguration)
            .subscribe({
                next: (configuration: IRatingConfiguration): void => {
                    this.configurationDetails = new Map(
                        configuration.detailArray
                            .map((detail: IRatingConfigurationDetail): [string, IRatingConfigurationDetail] => [detail.code, detail]));
                    this.changeDetector.markForCheck();
                },
                error: (errorResponse: Error): void => {
                    this.errorTracingService.captureException(errorResponse);
                },
            });
    }
}
